/**
 * PortalErrorBoundary — keeps a crash in one page from blanking the whole SPA.
 * Resets automatically when the route changes, so navigating away recovers.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function ErrorFallback({ home }) {
  const { t } = useTranslation(['common']);
  return (
    <div role="alert" data-testid="portal-error"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
               background: 'var(--surface)', padding: 24 }}>
      <div className="panel pad v g12" style={{ maxWidth: 460, textAlign: 'center' }}>
        <h1 className="t2">{t('error_boundary.title', { defaultValue: 'Something went wrong on this page' })}</h1>
        <p className="tb muted">{t('error_boundary.body', { defaultValue: 'The error has been logged. You can go back to the dashboard or reload the page.' })}</p>
        <div className="h g12" style={{ justifyContent: 'center' }}>
          <a className="btn" href={home}>{t('error_boundary.back', { defaultValue: 'Back to dashboard' })}</a>
          <button type="button" className="btn p" onClick={() => window.location.reload()}>{t('error_boundary.reload', { defaultValue: 'Reload' })}</button>
        </div>
      </div>
    </div>
  );
}

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[PortalErrorBoundary]', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const home = `/${this.props.resetKey.split('/')[1] || ''}`;
    return <ErrorFallback home={home} />;
  }
}

export default function PortalErrorBoundary({ children }) {
  const location = useLocation();
  return <Boundary resetKey={location.pathname}>{children}</Boundary>;
}
